/* eslint-disable ember/no-classic-components, ember/no-component-lifecycle-hooks, ember/no-computed-properties-in-native-classes, ember/require-computed-property-dependencies, ember/require-tagless-components, no-restricted-properties, no-unused-vars, warp-drive/no-legacy-request-patterns */
import { tracked } from '@glimmer/tracking';
import Component from '@ember/component';
// Ember imports
import config from '@linc-technologies/linced-common/utils/config';
import moment from 'moment';
import { alias } from '@ember/object/computed';
import { action, computed } from '@ember/object';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
// External imports
import Scopes from '@linc-technologies/linced-common/enums/scopes';
import SnapshotPdfType from '@linc-technologies/linced-common/enums/snapshot-pdf-type';
import SnapshotType from '@linc-technologies/linced-common/enums/snapshot-type';
import { openSnapshotPdf } from '@linc-technologies/linced-common/utils/snapshots';
import { pollLongRunningTask } from '@linc-technologies/linced-common/utils/long-tasks';

export default class GenerateSnapshot extends Component {
  @action
  setEndDate(value) {
    this.endDate = value;
  }

  @action
  setSelectedType(value) {
    this.selectedType = value;
  }

  @action
  setStartDate(value) {
    this.startDate = value;
  }

  @action
  setTerm(value) {
    this.term = value;
  }

  @action
  setYear(value) {
    this.year = value;
  }

  SnapshotPdfType = SnapshotPdfType;
  SnapshotType = SnapshotType;

  // Services
  @service
  dates;

  @service
  fetch;

  @service
  media;

  @service
  permissions;

  @service
  session;

  @service
  store;

  @service
  terms;

  @service
  toaster;

  // Attributes
  // displaySingularProcessMonitorProgress set to true shows a singular progress bar under buttons to
  // indicate progress of the long-running task
  displaySingularProcessMonitorProgress = false;

  // isPollFriendly indicates whether the task is okay to poll in the component, or
  // pass the polling for progress and results to the ProcessViewerMonitor component
  isPollFriendly = true;

  // learnerIds is the array of learners whom we're generating snapshots for
  learnerIds = [];

  // processMonitorId is returned once the request to generate snapshot is sent. We use it to poll for progress
  @tracked
  processMonitorId = '';

  // pollingResult is an array of snapshots returned once the long-running task is completed
  @tracked
  pollingResult = [];

  // onPollingResult is a passed in function that controls what we do with the polling result (snapshot). example: push it to UI, open a PDF, etc.
  onPollingResult = null;

  // timeframes are passed in timeframes for school's current academic year
  timeframes = [];

  // Snapshot generation
  @tracked startDate = '';

  @tracked endDate = '';
  @tracked generatingSnapshot = false;

  // overwrite indicates that we're in overwrite/re-generate mode for auto-generated ones
  @tracked overwrite = false;

  @tracked term = 1;
  @tracked year = moment().year();

  typeOptions = Object.freeze([
    // No on-demand for auto-generated overwrites
    SnapshotType.TERMLY,
    SnapshotType.BIANNUAL,
    SnapshotType.ANNUAL,
  ]);

  @tracked
  selectedType = SnapshotType.TERMLY;
  termOptions = Object.freeze([1, 2, 3, 4]);

  @alias('session.activeSchool')
  schoolId;

  // 2 is for school, 1 for group
  resourceType = 2;

  // resourceId is the id of the resource (group or school) for which we're generating snapshot sets
  resourceId = '';

  // selectedStartOption is the option selected in the dropdown for start dates
  @tracked
  selectedStartOption = null;

  // selectedEndOption is the option selected in the dropdown for end dates
  @tracked
  selectedEndOption = null;

  @tracked
  currentProcessMonitorId;

  // Computed
  // canViewGenerateNow determines if users can view the component and perform
  // on-demand snapshots generation
  @computed('session.scopes.[]')
  get canViewGenerateNow() {
    // Limit this functionality only to Tenant admins and higher
    return this.permissions.meet(Scopes.TENANT_ADMIN);
  }

  // canViewRegenerate determines if users can view the more advanced, dangerous
  // regeneration of auto-generated snapshots
  @computed('session.scopes.[]', 'overwrite')
  get canViewRegenerate() {
    // Limit the overwrite functionality only to Hero Managers and higher
    return this.permissions.meet(Scopes.LINCED_MANAGER) && this.overwrite;
  }

  @computed('overwrite', 'term', 'year', 'type')
  get isSnapshotToRegenerateSelected() {
    if (!this.overwrite) {
      return true;
    }
    return !isEmpty(this.term) || !isEmpty(this.year) || !isEmpty(this.type);
  }

  // Custom PaperInput validations
  @computed('startDate', 'endDate')
  get isStartDateValid() {
    if (isEmpty(this.startDate) || isEmpty(this.endDate)) {
      return true;
    }
    let parsedStartDate = moment(this.startDate, 'YYYY-MM-DD');
    let parsedEndDate = moment(this.endDate, 'YYYY-MM-DD');
    // Can't be past the end date
    return parsedStartDate.isBefore(parsedEndDate, 'day');
  }

  @computed('endDate')
  get isEndDateValid() {
    if (isEmpty(this.endDate)) {
      return true;
    }
    let parsedEndDate = moment(this.endDate, 'YYYY-MM-DD');
    let now = moment();
    // Can't be in the future as there is no data there
    return parsedEndDate.isSameOrBefore(now, 'd');
  }

  // canGenerateSnapshot enables/disables the "Generate now" button
  get canGenerateSnapshot() {
    return (
      !this.generatingSnapshot &&
      !isEmpty(this.startDate) &&
      !isEmpty(this.endDate) &&
      this.isStartDateValid &&
      this.isEndDateValid
    );
  }

  @computed('termDates.[]', 'alternativeTimeframeDates.[]')
  get schoolAcademicDates() {
    if (!isEmpty(this.alternativeTimeframeDates)) {
      // NSW are likely to use alternative assessment timeframes
      return this.alternativeTimeframeDates;
    }
    // Default to terms as all schools have this defined
    return this.termDates;
  }

  @computed(
    'terms.terms.[]',
    'terms.currentAcademicYearTerms.@each.{term,start,end}',
  )
  get termDates() {
    // NOTE: the classic version of this CP was declared `async function`, so its
    // value was a Promise. A native getter cannot be async, so the body is kept
    // in an async IIFE to preserve that exactly. (The `await` here is on a
    // synchronous array chain and the consumer below treats the result as an
    // array, so the original `async` looks accidental - worth revisiting.)
    return (async () => {
      if (isEmpty(this.terms.terms)) {
        return [];
      }
      // Accounts for in-between terms, too
      let currentTerm = this.terms.currentTerm;
      // Filter out terms in the future - there's no data to generate for, so we don't
      // need them
      return await this.terms
        .get('currentAcademicYearTerms')
        .filter((term) => term.term <= currentTerm.term)
        .map((term) => {
          return {
            label: `Term ${term.term}`,
            startDate: term.start,
            endDate: term.end,
          };
        });
    })();
  }

  @computed('timeframes.[]')
  get alternativeTimeframeDates() {
    if (isEmpty(this.timeframes)) {
      return [];
    }
    return this.timeframes
      .slice()
      .sort((a, b) => a.startDateUnix - b.startDateUnix)
      .map((timeframe) => {
        return {
          label: timeframe.columnLabel,
          startDate: timeframe.startDate,
          endDate: timeframe.endDate,
        };
      });
  }

  // Hooks
  async didInsertElement() {
    super.didInsertElement(...arguments);
    await this.terms.waitUntilLoaded();
  }

  didReceiveAttrs() {
    super.didReceiveAttrs(...arguments);
    if (this.pollingResult) {
      this.handlePollingResult();
    }
  }

  // Functions
  async handlePollingResult() {
    if (isEmpty(this.pollingResult)) {
      return;
    }
    if (this.pollingResult.length > 1) {
      // Multiple snapshots - just refresh the model
      this.refreshModel();
      this.reset();
      return;
    }
    // If it's only one snapshot for one person - open in a new tab right away
    try {
      // Single report download from the generated snapshot
      let snapshot = await this.store.findRecord(
        `snapshots/snapshot`,
        this.pollingResult[0],
      );
      // We've only just generated it so there will be one report
      let mediaId = snapshot.pdfs.firstObject.mediaId;
      let file = await this.store.findRecord('media', mediaId);
      openSnapshotPdf(file);
      this.addSnapshot(snapshot);
      this.reset();
    } catch (error) {
      this.toaster.error(
        'Failed to generate or open the PDF. Please refresh the page and try again from the table below.',
      );
    }
  }

  setProcessMonitorId(monitorId) {
    this.currentProcessMonitorId = monitorId;
  }

  @action
  async generateSnapshot() {
    this.generatingSnapshot = true;
    let url = `${config.APP.apiHost}/snapshots/${config.APP.apiPath}/snapshots/:generate`;
    let data = {
      schoolId: this.schoolId,
      startDate: this.startDate,
      endDate: this.endDate,
      type: this.selectedType,
    };
    if (!isEmpty(this.learnerIds)) {
      data.learnerIds = this.learnerIds;
    }
    if (this.overwrite) {
      // Overwrite for auto-generated is active - add some extra metadata
      data.year = parseInt(this.year, 10);
      if (this.selectedType === SnapshotType.TERMLY) {
        data.term = parseInt(this.term, 10);
      }
    }
    if (!isEmpty(this.resourceId)) {
      // We're generating a snapshot set - extra details needed
      data.resourceId = this.resourceId;
      data.resourceType = this.resourceType;
    }
    if (this.selectedType === SnapshotType.ON_DEMAND) {
      data.generatedById = this.session.person.id;
      data.year = moment(this.endDate).year();
    }
    try {
      // Snapshots generation
      let response = await this.fetch.post(url, { data });
      this.setProcessMonitorId(response.processMonitorId);
      if (!this.isPollFriendly) {
        // Non-poll-friendly take too long to poll in here
        // We just use the processMonitorId in another component specifically designed
        // to poll for big bois
        this.toaster.success(
          'Started generating reports. It might take awhile depending on the number of learners.',
        );
        this.generatingSnapshot = false;
        return;
      }
      let pollingResult = await pollLongRunningTask(
        response.processMonitorId,
        this.store,
      );
      this.pollingResult = pollingResult;
      await this.handlePollingResult();
      this.generatingSnapshot = false;
    } catch (error) {
      this.toaster.error(
        `Error generating the ${this.session.school.snapshotsWording}. Please refresh the page and try again.`,
      );
      this.generatingSnapshot = false;
    }
  }

  @action
  selectStartDate(selectedSchoolDateObject) {
    if (!isEmpty(selectedSchoolDateObject)) {
      let endDate = selectedSchoolDateObject.endDate;
      let today = moment().format('YYYY-MM-DD');
      if (
        moment(selectedSchoolDateObject.endDate, 'YYYY-MM-DD').isAfter(
          today,
          'day',
        )
      ) {
        // We cannot have an end date in the future, so replace the timeframe end date with today's date instead
        endDate = today;
      }
      this.selectedStartOption = selectedSchoolDateObject;
      this.startDate = selectedSchoolDateObject.startDate;
      this.endDate = endDate;
    }
  }

  @action
  reset() {
    this.startDate = '';
    this.endDate = '';
    this.term = '';
    this.year = '';
    this.processMonitorId = '';
    this.pollingResult = [];
    this.selectedStartOption = null;
    this.selectedEndOption = null;
  }
}
